import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { goBack as goBackReactRouter, push } from 'react-router-redux';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import Box from '@quintoandar/cozy-core/Box';
import Button from '@quintoandar/cozy-core/Button';
import Form from '@quintoandar/cozy-core/Form';
import Radio from '@quintoandar/cozy-core/Radio';
import RadioGroup from '@quintoandar/cozy-core/RadioGroup';
import Typography from '@quintoandar/cozy-core/Typography';
import Snackbar from '@quintoandar/cozy-core/Snackbar';

import { fetchQuarantinedCitiesList } from 'api/cities';
import { rescheduleFlexibleSession } from 'api/photo-session';
import AppBar from 'components/AppBar';
import { LoadingPage } from 'components/LoadingPage';
import PageWrapper from 'components/PageWrapper';
import globalMessages from 'messages/globalMessages';
import ROUTES from 'routes/constants';
import { getUser } from 'utils/user';
import ErrorBoundary from 'components/ErrorBoundary';
import { logUncaught } from 'utils/log';

import messages from './messages';
import {
  photoSessionRescheduleConfirmOnClickAnalytics,
  photoSessionRescheduleSuccessViewedAnalytics,
} from './actions';
import { buildOptions, buildButtons, buildFlexiblePayload } from './utils';
import { SESSION_TYPE } from './constants';
import { SuccessfulRescheduleDialog } from './SuccessfulRescheduleDialog';
import useAvailablePhotoJobTypes from '../../PhotoSessions/useAvailablePhotoJobTypes';
import useRescheduleSessionDetail from './useRescheduleSessionDetail';
import { ErrorState } from './RescheduleFeedback';

const radioGroupName = 'sessionTypesRadio';

const buildRadioItem = option => (
  <Radio
    name={option.name}
    label={option.label}
    description={option.description}
    value={option.value}
    disabled={option.disabled}
  />
);

export const Reschedule = ({ params }) => {
  const dispatch = useDispatch();
  const detail = useRescheduleSessionDetail(params.id);
  const { photoSessionDetail } = detail;

  const goToBasePath = useCallback(
    () => {
      dispatch(push(ROUTES.BASE_PATH));
    },
    [dispatch],
  );
  const goToSchedulesDates = useCallback(
    () => {
      dispatch(push(ROUTES.RESCHEDULE_DATE_AND_TIME(params.id)));
    },
    [dispatch, params],
  );
  const goBack = useCallback(
    () => {
      dispatch(goBackReactRouter());
    },
    [dispatch],
  );
  const trackRescheduleOnConfirmation = useCallback(
    newSessionType => {
      dispatch(
        photoSessionRescheduleConfirmOnClickAnalytics({
          photoSessionId: params.id,
          newSessionType,
        }),
      );
    },
    [dispatch, params],
  );
  const trackRescheduleSuccessViewed = useCallback(
    samePhotographerResult => {
      dispatch(
        photoSessionRescheduleSuccessViewedAnalytics({
          photoSessionId: params.id,
          samePhotographerResult,
        }),
      );
    },
    [dispatch, params],
  );

  /* istanbul ignore next */
  const [quarantinedCities, setQuarantinedCities] = useState([]);
  const [sessionType, setSessionType] = useState();
  const [newSessionAgentId, setNewSessionAgentId] = useState();
  const [hasRescheduleError, setHasRescheduleError] = useState(false);
  const [isSubmited, setIsSubmited] = useState(false);

  useEffect(() => {
    const fetchQuarantinedCities = async () => {
      try {
        const quarantinedCitiesList = await fetchQuarantinedCitiesList();
        setQuarantinedCities(quarantinedCitiesList);
      } catch (error) {
        // Not blocking: without the list every session type stays enabled.
        logUncaught(error, { context: 'fetchQuarantinedCitiesList' });
      }
    };

    fetchQuarantinedCities();
  }, []);

  const house = (photoSessionDetail && photoSessionDetail.house) || {};
  const { userAgentId } = getUser();

  const handleOnClickContinue = async () => {
    trackRescheduleOnConfirmation(sessionType);
    if (sessionType === SESSION_TYPE.FIXED) {
      goToSchedulesDates();
    } else {
      setIsSubmited(true);
      try {
        const flexiblePayload = buildFlexiblePayload({
          photoSessionDetail,
          sessionType,
        });
        const response = await rescheduleFlexibleSession({
          propertyId: house.id,
          body: flexiblePayload,
        });
        setIsSubmited(false);
        setNewSessionAgentId(response.photographerId);
        trackRescheduleSuccessViewed(userAgentId === response.photographerId);
      } catch (error) {
        logUncaught(error, { context: 'rescheduleFlexibleSession', propertyId: house.id });
        setIsSubmited(false);
        setHasRescheduleError(true);
      }
    }
  };

  const onCloseSnackBarError = () => setHasRescheduleError(false);

  const handleSessionTypeChange = ({ target: { value } }) =>
    setSessionType(value);

  const isQuarantinedCity = house && quarantinedCities.includes(house.city);
  const { availableTypes } = useAvailablePhotoJobTypes(house.id);

  const sessionTypeOptions = buildOptions({
    isQuarantinedCity,
    availableTypes,
  });

  const actionButtons = buildButtons({
    handleOnClickContinue,
    handleOnClickBack: goBack,
    disableContinueBtn: !sessionType,
    continueButtonLabel: messages.continueButtonLabel,
  });
  const subtitle = isQuarantinedCity
    ? messages.quarantineSubtitle
    : messages.subtitle;
  const isSessionRescheduled = !!newSessionAgentId;
  const isSameAgent = newSessionAgentId === userAgentId;

  const renderBody = () => {
    if (detail.isError) {
      return <ErrorState title={globalMessages.sessionLoadError} onRetry={detail.refetch} />;
    }

    if (detail.isLoading) {
      return <LoadingPage />;
    }

    return (
      <PageWrapper>
        <Box mt={1}>
          <Box mb={1}>
            <Typography level="title">
              <FormattedMessage {...messages.title} />
            </Typography>
          </Box>

          <Box mb={1}>
            <Typography kind="lighter">
              <FormattedMessage {...subtitle} />
            </Typography>
          </Box>

          <Form>
            <RadioGroup
              name={radioGroupName}
              aria-label={radioGroupName}
              onChange={handleSessionTypeChange}
            >
              {sessionTypeOptions.map(buildRadioItem)}
            </RadioGroup>
          </Form>

          <Box display="flex" justifyContent="flex-end" width="100%" mt={3}>
            <Box mr={2}>
              <Button
                variant="quiet"
                onClick={actionButtons[1].onClick}
                disabled={actionButtons[1].disabled || isSubmited}
              >
                <FormattedMessage {...actionButtons[1].label} />
              </Button>
            </Box>

            <Button
              onClick={actionButtons[0].onClick}
              disabled={actionButtons[0].disabled}
              isLoading={isSubmited}
            >
              <FormattedMessage {...actionButtons[0].label} />
            </Button>
          </Box>

          <SuccessfulRescheduleDialog
            isSamePhotographer={isSameAgent}
            onConfirmationAction={goToBasePath}
            isDialogOpen={isSessionRescheduled}
          />

          <ErrorBoundary errorComponent={null} logError={logUncaught}>
            <Snackbar
              color="error"
              open={hasRescheduleError}
              message={
                <FormattedMessage {...messages.rescheduleErrorMessage} />
              }
              onClose={onCloseSnackBarError}
            />
          </ErrorBoundary>
        </Box>
      </PageWrapper>
    );
  };

  return (
    <Fragment>
      <AppBar onBack={goBack} title={messages.headerTitle} />
      {renderBody()}
    </Fragment>
  );
};

Reschedule.propTypes = {
  params: PropTypes.shape({
    id: PropTypes.string,
  }),
};

export default Reschedule;
